import { AdminShell } from "@project/admin/components/admin-shell";
import { ApiResourceTable } from "@project/admin/components/api-resource-table";
import type { Field } from "@project/admin/components/resource-table";
import { donationMasterService } from "@project/services/admin/donation-master.service";
import type {
  DonationMasterNamePayload,
  DonationMasterNameRecord,
  DonationSubCategoryRecord,
} from "@project/types/donation-master";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@project/components/ui/tabs";
import { Gift, Grid3X3, ListTree, Tag, Users } from "lucide-react";

const nameMasterFields: Field[] = [
  {
    name: "name",
    label: "Title / Name",
    required: true,
    showInTable: true,
    placeholder: "Enter name",
  },
  {
    name: "is_active",
    label: "Status",
    type: "boolean",
    showInTable: true,
  },
];

const subCategoryFields: Field[] = [
  {
    name: "main_category",
    label: "Main Category",
    required: true,
    showInTable: true,
    lookup: {
      table: "donation_main_category_master",
      labelField: "name",
    },
  },
  {
    name: "title",
    label: "Title / Name",
    required: true,
    showInTable: true,
    placeholder: "Food Support",
  },
  {
    name: "is_active",
    label: "Status",
    type: "boolean",
    showInTable: true,
  },
];

function toNamePayload(
  payload: Record<string, unknown>
): DonationMasterNamePayload {
  return {
    name: String(payload.name ?? "").trim(),
    is_active: payload.is_active === true || payload.is_active === "true",
  };
}

type NameMasterTabProps = {
  title: string;
  list: (
    query: Parameters<typeof donationMasterService.donorType.listPage>[0]
  ) => ReturnType<typeof donationMasterService.donorType.listPage>;
  create: (
    payload: DonationMasterNamePayload
  ) => Promise<DonationMasterNameRecord>;
  update: (
    id: number,
    payload: Partial<DonationMasterNamePayload>
  ) => Promise<DonationMasterNameRecord | undefined>;
  remove: (id: number) => Promise<void>;
};

function NameMasterTab({
  title,
  list,
  create,
  update,
  remove,
}: NameMasterTabProps) {
  return (
    <ApiResourceTable<DonationMasterNameRecord>
      title={title}
      labelField="name"
      fields={nameMasterFields}
      searchFields={["name"]}
      defaultValues={{ is_active: true }}
      statusFilterOptions={[
        { value: "active", label: "Active" },
        { value: "inactive", label: "Inactive" },
      ]}
      list={list}
      create={payload => create(toNamePayload(payload))}
      update={(id, payload) => update(id, toNamePayload(payload))}
      remove={remove}
    />
  );
}

export default function Page() {
  return (
    <AdminShell
      title="Donation Masters"
      subtitle="Manage donor types, donor categories, donation types, main categories, and sub categories in one place"
    >
      <Tabs defaultValue="donor-type" variant="line" className="space-y-4">
        <div className="-mx-1 overflow-x-auto px-1 pb-1">
          <TabsList>
            <TabsTrigger value="donor-type" className="shrink-0">
              <Users className="h-4 w-4 shrink-0" />
              Donor Type
            </TabsTrigger>
            <TabsTrigger value="donor-category" className="shrink-0">
              <Tag className="h-4 w-4 shrink-0" />
              Donor Category
            </TabsTrigger>
            <TabsTrigger value="donation-type" className="shrink-0">
              <Gift className="h-4 w-4 shrink-0" />
              Donation Type
            </TabsTrigger>
            <TabsTrigger value="main-category" className="shrink-0">
              <Grid3X3 className="h-4 w-4 shrink-0" />
              Donation Main Category
            </TabsTrigger>
            <TabsTrigger value="sub-category" className="shrink-0">
              <ListTree className="h-4 w-4 shrink-0" />
              Donation Sub Category
            </TabsTrigger>
          </TabsList>
        </div>


        <TabsContent value="donor-type" className="mt-0">
          <NameMasterTab
            title="Donor Type"
            list={query => donationMasterService.donorType.listPage(query)}
            create={payload => donationMasterService.donorType.create(payload)}
            update={(id, payload) =>
              donationMasterService.donorType.update(id, payload)
            }
            remove={id => donationMasterService.donorType.delete(id)}
          />
        </TabsContent>

        <TabsContent value="donor-category" className="mt-0">
          <NameMasterTab
            title="Donor Category"
            list={query => donationMasterService.donorCategory.listPage(query)}
            create={payload =>
              donationMasterService.donorCategory.create(payload)
            }
            update={(id, payload) =>
              donationMasterService.donorCategory.update(id, payload)
            }
            remove={id => donationMasterService.donorCategory.delete(id)}
          />
        </TabsContent>

        <TabsContent value="donation-type" className="mt-0">
          <NameMasterTab
            title="Donation Type"
            list={query => donationMasterService.donationType.listPage(query)}
            create={payload =>
              donationMasterService.donationType.create(payload)
            }
            update={(id, payload) =>
              donationMasterService.donationType.update(id, payload)
            }
            remove={id => donationMasterService.donationType.delete(id)}
          />
        </TabsContent>

        <TabsContent value="main-category" className="mt-0">
          <NameMasterTab
            title="Donation Main Category"
            list={query => donationMasterService.mainCategory.listPage(query)}
            create={payload =>
              donationMasterService.mainCategory.create(payload)
            }
            update={(id, payload) =>
              donationMasterService.mainCategory.update(id, payload)
            }
            remove={id => donationMasterService.mainCategory.delete(id)}
          />
        </TabsContent>

        <TabsContent value="sub-category" className="mt-0">
          <ApiResourceTable<DonationSubCategoryRecord>
            title="Donation Sub Category"
            labelField="title"
            fields={subCategoryFields}
            searchFields={["title"]}
            defaultValues={{ is_active: true }}
            statusFilterOptions={[
              { value: "active", label: "Active" },
              { value: "inactive", label: "Inactive" },
            ]}
            lookupSearchLoaders={{
              main_category: query =>
                donationMasterService.listMainCategoriesForLookup(query),
            }}
            list={query => donationMasterService.listSubCategoriesPage(query)}
            create={payload =>
              donationMasterService.createSubCategory({
                main_category: Number(payload.main_category),
                title: String(payload.title ?? "").trim(),
                is_active:
                  payload.is_active === true || payload.is_active === "true",
              })
            }
            update={(id, payload) =>
              donationMasterService.updateSubCategory(id, {
                main_category:
                  payload.main_category !== undefined
                    ? Number(payload.main_category)
                    : undefined,
                title:
                  payload.title !== undefined
                    ? String(payload.title).trim()
                    : undefined,
                is_active:
                  payload.is_active !== undefined
                    ? payload.is_active === true || payload.is_active === "true"
                    : undefined,
              })
            }
            remove={id => donationMasterService.deleteSubCategory(id)}
          />
        </TabsContent>
      </Tabs>
    </AdminShell>
  );
}
